import { Card, CardContent } from "@/components/ui/card";
import { DollarSign } from "lucide-react";

interface RevenueRow {
  label: string;
  amount: number;
  color: string;
  count: number;
}

interface RevenueBreakdownProps {
  rows: RevenueRow[];
  total: number;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function RevenueBreakdown({ rows, total }: RevenueBreakdownProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" />
          REVENUE BREAKDOWN
        </h4>

        {/* Stacked bar */}
        <div className="h-8 rounded-lg overflow-hidden flex mb-4">
          {rows.map((row) => {
            const pct = total > 0 ? (row.amount / total) * 100 : 0;
            if (pct < 1) return null;
            return (
              <div
                key={row.label}
                className="h-full flex items-center justify-center transition-all duration-500"
                style={{
                  width: `${pct}%`,
                  backgroundColor: row.color,
                  minWidth: pct > 0 ? 4 : 0,
                }}
              >
                {pct > 15 && (
                  <span className="text-xs font-medium text-white">{pct.toFixed(0)}%</span>
                )}
              </div>
            );
          })}
          {total === 0 && (
            <div className="h-full w-full bg-gray-100 flex items-center justify-center">
              <span className="text-xs text-muted-foreground">No data</span>
            </div>
          )}
        </div>

        {/* Legend rows */}
        <div className="space-y-1.5">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: row.color }}
                />
                <span className="text-xs">{row.label}</span>
                <span className="text-xs text-muted-foreground">({row.count})</span>
              </div>
              <span className="text-xs font-medium tabular-nums">{fmt(row.amount)}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t">
          <span className="text-xs font-semibold">Total Pipeline</span>
          <span className="text-sm font-bold tabular-nums">{fmt(total)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
